/**
 * UU Evidence & Document Cleanup
 *
 * When a benefit field's amount is cleared to 0 and a benefit start date
 * (evidence period) and/or uploaded document still exist for that field, we
 * prompt the user before saving:
 *
 *   "Remove" → delete evidence date + document, then save the cleared amount.
 *   "Keep"   → revert the amount, stay on the page, don't save.
 *
 * This module provides detection and deletion helpers used by both
 * updateBudgetPage (batch, multi-field) and EditFieldDialog (single field).
 */

import {
  UuEvidenceService,
  DocumentsPageService,
} from "@/shared/api/services";
import type { UcEvidencePeriod } from "@/shared/api/types";
import ieFormLogic from "@/components/uu/uuEngine/ieFormLogic.json";
import { isUuFieldName } from "@/components/uu/config/uuFieldConfig";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

interface IeFormField {
  ieFormFieldName: string;
  requiresBenefitStartDate?: boolean;
  dateLabel?: string | null;
  evidenceTitle?: string;
  question?: string;
}

/**
 * All IE-form field names that carry a benefit start date.
 * Derived from ieFormLogic.json → fields with requiresBenefitStartDate === true.
 */
export const UU_DATE_FIELDS: ReadonlySet<string> = new Set(
  ((ieFormLogic as { fields: IeFormField[] }).fields ?? [])
    .filter((f) => f.requiresBenefitStartDate === true)
    .map((f) => f.ieFormFieldName)
);

/**
 * Human-readable label for each UU date field (used in confirmation dialogs).
 */
export const UU_DATE_FIELD_LABELS: Readonly<Record<string, string>> = (() => {
  const map: Record<string, string> = {};
  for (const f of (ieFormLogic as { fields: IeFormField[] }).fields ?? []) {
    if (f.requiresBenefitStartDate === true) {
      // Prefer evidenceTitle, fall back to dateLabel, then question
      map[f.ieFormFieldName] =
        f.evidenceTitle?.replace(/ proof$/i, "") ??
        f.dateLabel ??
        f.question ??
        f.ieFormFieldName;
    }
  }
  return map;
})();

/**
 * Maps each UU date field to the budget category it belongs to.
 * "benefits" for most, "pension" for pensionCredit.
 */
export const UU_FIELD_TO_CATEGORY: Readonly<Record<string, string>> = {
  universalCredit: "benefits",
  empsupportallowancestatutory: "benefits",
  jobSeekerAllowance: "benefits",
  incomeSupport: "benefits",
  pensionCredit: "pension",
  housingBenefitLocalAllowance: "benefits",
  childBenefit: "benefits",
  disabilityAllowance: "benefits",
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CleanupRecord {
  /** The schema field name (e.g. "universalCredit") */
  fieldName: string;
  /** Human-readable label for the field (e.g. "Universal Credit") */
  label: string;
  /** The evidence period row id, or null if no evidence exists */
  evidenceId: number | null;
  /** The document row id, or null if no document exists */
  documentId: number | null;
  /** Whether there is an evidence date to delete */
  hasDate: boolean;
  /** Whether there is an uploaded document to delete */
  hasDocument: boolean;
}

export interface CleanupDeleteFailure {
  fieldName: string;
  type: "evidence" | "document";
  id: number;
  error: unknown;
}

// ---------------------------------------------------------------------------
// Detection (batch — for updateBudgetPage)
// ---------------------------------------------------------------------------

/**
 * Checks whether any UU date fields in the given budget category have their
 * amount set to 0/empty while a backend evidence period and/or document exists.
 *
 * @param customerId - Customer identifier
 * @param formData   - The current form data for the section.
 *                     Each benefit field is expected as `{ amount, frequency, ... }`.
 * @param category   - The budget category being saved (e.g. "benefits", "pension").
 * @returns Array of cleanup records. Empty if nothing needs attention.
 */
export async function detectCleanupNeeded(
  customerId: string | number,
  formData: Record<string, unknown>,
  category: string
): Promise<CleanupRecord[]> {
  // Determine which UU date fields belong to this category
  const relevantFields = Array.from(UU_DATE_FIELDS).filter(
    (fieldName) => UU_FIELD_TO_CATEGORY[fieldName] === category
  );

  if (relevantFields.length === 0) {
    return [];
  }

  // Find fields where the amount is explicitly 0 or empty.
  // Only consider fields that are actually present in formData — a field that
  // simply isn't rendered in the current form (key absent) should NOT be
  // treated as "cleared" to avoid false-positive prompts.
  const clearedFields: string[] = [];
  for (const fieldName of relevantFields) {
    if (!(fieldName in formData)) {
      // Field is not part of the current form at all — skip
      continue;
    }
    const fieldValue = formData[fieldName];
    if (
      fieldValue &&
      typeof fieldValue === "object" &&
      !Array.isArray(fieldValue)
    ) {
      const amount = (fieldValue as Record<string, unknown>).amount;
      if (isAmountEmpty(amount)) {
        clearedFields.push(fieldName);
      }
    } else if (fieldValue === null || fieldValue === undefined || fieldValue === "") {
      // Field key exists but value is null/undefined/empty — treat as cleared
      clearedFields.push(fieldName);
    }
    // If fieldValue is something else (e.g. a string/number), skip — not a
    // standard question-group shape, nothing to clean up.
  }

  if (clearedFields.length === 0) {
    return [];
  }

  // Fetch evidence periods and documents in parallel
  const [evidencePeriods, documents] = await fetchEvidenceAndDocuments(
    customerId
  );

  // Build the document lookup: shareId/entrySource → document record
  const documentByField = buildDocumentLookup(documents);

  // Match cleared fields to existing evidence/document records
  const results: CleanupRecord[] = [];
  for (const fieldName of clearedFields) {
    const evidenceMatch = evidencePeriods.find(
      (ep) => ep.entrySource === fieldName
    );
    const docMatch = documentByField[fieldName] ?? null;

    const hasDate = Boolean(evidenceMatch);
    const hasDocument = Boolean(docMatch);

    // Only include if there's something to clean up
    if (hasDate || hasDocument) {
      results.push({
        fieldName,
        label: UU_DATE_FIELD_LABELS[fieldName] ?? fieldName,
        evidenceId: evidenceMatch?.id ?? null,
        documentId: docMatch?.id ?? null,
        hasDate,
        hasDocument,
      });
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// Detection (single field — for EditFieldDialog)
// ---------------------------------------------------------------------------

/**
 * Detects cleanup needed for a single field.
 * Used by EditFieldDialog which operates on one field at a time.
 *
 * @param customerId - Customer identifier
 * @param fieldKey   - The schema field name (e.g. "universalCredit")
 * @param amount     - The amount value being saved
 * @returns The cleanup record, or null if nothing to do.
 */
export async function detectSingleFieldCleanup(
  customerId: string | number,
  fieldKey: string,
  amount: unknown
): Promise<CleanupRecord | null> {
  // Only check UU date fields
  if (!UU_DATE_FIELDS.has(fieldKey)) {
    return null;
  }

  // Only check if amount is empty/0
  if (!isAmountEmpty(amount)) {
    return null;
  }

  // Fetch evidence and documents
  const [evidencePeriods, documents] = await fetchEvidenceAndDocuments(
    customerId
  );

  const evidenceMatch = evidencePeriods.find(
    (ep) => ep.entrySource === fieldKey
  );
  const documentByField = buildDocumentLookup(documents);
  const docMatch = documentByField[fieldKey] ?? null;

  const hasDate = Boolean(evidenceMatch);
  const hasDocument = Boolean(docMatch);

  if (!hasDate && !hasDocument) {
    return null;
  }

  return {
    fieldName: fieldKey,
    label: UU_DATE_FIELD_LABELS[fieldKey] ?? fieldKey,
    evidenceId: evidenceMatch?.id ?? null,
    documentId: docMatch?.id ?? null,
    hasDate,
    hasDocument,
  };
}

// ---------------------------------------------------------------------------
// Deletion
// ---------------------------------------------------------------------------

/**
 * Deletes evidence periods and documents for the given cleanup records.
 *
 * @returns Array of failures (empty on full success).
 */
export async function deleteCleanupRecords(
  records: CleanupRecord[],
  customerId: string | number
): Promise<CleanupDeleteFailure[]> {
  const failures: CleanupDeleteFailure[] = [];

  for (const record of records) {
    // Delete evidence period
    if (record.hasDate && record.evidenceId != null) {
      try {
        await UuEvidenceService.deleteEvidencePeriod(
          record.evidenceId,
          customerId
        );
      } catch (error) {
        failures.push({
          fieldName: record.fieldName,
          type: "evidence",
          id: record.evidenceId,
          error,
        });
      }
    }

    // Delete document
    if (record.hasDocument && record.documentId != null) {
      try {
        await DocumentsPageService.deleteCustomerDocument(
          customerId,
          record.documentId
        );
      } catch (error) {
        failures.push({
          fieldName: record.fieldName,
          type: "document",
          id: record.documentId,
          error,
        });
      }
    }
  }

  return failures;
}

// ---------------------------------------------------------------------------
// Confirmation message builder
// ---------------------------------------------------------------------------

/**
 * Builds a human-readable confirmation message for the cleanup dialog.
 */
export function buildCleanupConfirmMessage(
  records: CleanupRecord[]
): string {
  if (records.length === 1) {
    const r = records[0];
    const parts: string[] = [];
    if (r.hasDate) parts.push("benefit start date");
    if (r.hasDocument) parts.push("uploaded document");
    const what = parts.join(" and ");
    return `The ${what} for "${r.label}" will be removed because the amount has been cleared. Do you want to continue?`;
  }

  // Multiple fields
  const lines: string[] = [];
  for (const r of records) {
    const parts: string[] = [];
    if (r.hasDate) parts.push("date");
    if (r.hasDocument) parts.push("document");
    lines.push(`\u2022 ${r.label} (${parts.join(" & ")})`);
  }
  return `The following benefit start dates and/or documents will be removed because the amounts have been cleared:\n${lines.join("\n")}\n\nDo you want to continue?`;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface DocumentRecord {
  id: number;
  shareId?: string;
  entrySource?: string;
}

async function fetchEvidenceAndDocuments(
  customerId: string | number
): Promise<[UcEvidencePeriod[], DocumentRecord[]]> {
  let evidencePeriods: UcEvidencePeriod[] = [];
  let rawDocuments: unknown = [];

  try {
    const [evidenceResult, documentsResult] = await Promise.all([
      UuEvidenceService.getCustomerEvidencePeriods(customerId).catch(() => []),
      DocumentsPageService.getCustomerDocuments(customerId).catch(() => []),
    ]);
    evidencePeriods = Array.isArray(evidenceResult) ? evidenceResult : [];

    // Documents come back from the API in various shapes; normalise to array
    const docsRaw = documentsResult;
    if (Array.isArray(docsRaw)) {
      rawDocuments = docsRaw;
    } else if (
      docsRaw &&
      typeof docsRaw === "object" &&
      "data" in (docsRaw as Record<string, unknown>) &&
      Array.isArray((docsRaw as Record<string, unknown>).data)
    ) {
      rawDocuments = (docsRaw as Record<string, unknown>).data;
    }
  } catch {
    console.warn(
      "[uuEvidenceCleanup] Unable to fetch evidence/documents; skipping cleanup check."
    );
  }

  // Cast raw documents to our minimal shape
  const documents: DocumentRecord[] = (
    Array.isArray(rawDocuments) ? rawDocuments : []
  )
    .filter(
      (d: unknown): d is Record<string, unknown> =>
        typeof d === "object" && d !== null && typeof (d as Record<string, unknown>).id === "number"
    )
    .map((d) => ({
      id: d.id as number,
      shareId: typeof d.shareId === "string" ? d.shareId : undefined,
      entrySource:
        typeof d.entrySource === "string" ? d.entrySource : undefined,
    }));

  return [evidencePeriods, documents];
}

/**
 * Builds a lookup from field name → document record, using the same matching
 * logic as useUuFieldController: shareId (uu-{fieldName}) or entrySource.
 */
function buildDocumentLookup(
  documents: DocumentRecord[]
): Record<string, DocumentRecord> {
  const map: Record<string, DocumentRecord> = {};

  for (const doc of documents) {
    // Priority 1: shareId starting with "uu-"
    if (doc.shareId && doc.shareId.startsWith("uu-")) {
      const fieldName = doc.shareId.slice(3); // strip "uu-" prefix
      if (isUuFieldName(fieldName) && !map[fieldName]) {
        map[fieldName] = doc;
      }
    }

    // Priority 2: entrySource
    if (doc.entrySource && isUuFieldName(doc.entrySource) && !map[doc.entrySource]) {
      map[doc.entrySource] = doc;
    }
  }

  return map;
}

function isAmountEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  if (typeof value === "number") {
    return value === 0;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "").replace(/[^\d.-]/g, "").trim();
    if (cleaned === "" || cleaned === "-") {
      return true;
    }
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) && parsed === 0;
  }
  return false;
}

// Re-export for backward compat (used in existing code)
export {
  type CleanupRecord as EvidenceCleanupRecord,
  type CleanupDeleteFailure as EvidenceDeleteFailure,
};
